import React, { useCallback } from 'react';
import {
    Card,
    ExamApplicationStatus,
    ExamStatus,
    ExamDetailsTabs,
    LoaderBtn,
    WarningModal,
    AppType,
} from 'components';
import { OnlineModal, DetailsTimeLeft } from '../../components';
import { ExamApplication, ExamType } from '../../api/ApiClient';
import { ExamApplicationDetailsContainer } from '../ExamApplicationDetailsContainer';
import { ApplicationExamDetailsContainer } from '../ApplicationExamDetailsContainer';
import { connect } from 'react-redux';
import { goBack, push } from 'connected-react-router';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { useLocalErrorHandler } from 'hooks';
import { VideoType } from '_helpers';
import styles from './ExamDetailsContainer.module.scss';

const ExamDetailsContainer = ({
    examId,
    onFetchExamDetails,
    onFetchExamApplications,
    onClearExamApplications,
    onSendSovietPasswordAgain,
    onSendNokPasswordAgain,
    onSendAkPasswordAgain,
    onSubmitSovietSignCode,
    onSubmitNokSignCode,
    onSubmitAkSignCode,
    onClearExamDetails,
    onStartExam,
    onGoBack,
    onPush,
    setErrorHandler,
    examDetails,
    examApplicationsList,
    startExamProcessing,
    sendCodeProcessing,
    smsCodeStatus,
    apiError,
    auth,
    messages,
    getMessages,
    sendMessage,
    webRTC,
    startReceiver,
    switchVideoType,
    startReceiverQueue,
    acceptedApplications,
    openedOnlineModal,
    setOpenedOnlineModal,
    onMarkAsRead,
    stopAll,
    stopReceiver,
    onAdmitToExam,
    finishPracticeDefense,
    clearMessages,
    unread,
    examApplicationDetails,
    onFetchExamApplicationDetails,
}: Props) => {
    useLocalErrorHandler(setErrorHandler);

    const [showWarningModal, setShowWarningModal] = React.useState(false);
    const [showStartExamModal, setShowStartExamModal] = React.useState(false);
    const [showApplicantModal, setShowApplicantModal] = React.useState(false);
    const [processing, setProcessing] = React.useState(false);
    const [selectedApplicant, setSelectedApplicant] = React.useState<ExamApplication | null>(null);
    const isOnlineModalStatus = Boolean(examDetails
        && examDetails.type === ExamType.Online
        && (examDetails.status === ExamStatus.IsPassing || examDetails.status === ExamStatus.ApplyingFinished));

    const usePrevious = (value: ExamStatus | null) => {
        const prevExamStatus = React.useRef<ExamStatus | null>(null);

        React.useEffect(() => {
            prevExamStatus.current = value;
        });
        return prevExamStatus.current;
    };

    const prevStatus = usePrevious(examDetails ? examDetails.status : null);

    React.useEffect(() => {
        if (examDetails && (examDetails.status !== ExamStatus.IsPassing && examDetails.status !== ExamStatus.ApplyingFinished)) {
            stopAll();
        }
        return () => {
            stopAll();
        };
    }, [examDetails]);

    React.useEffect(() => {
        const fetch = async () => {
            setProcessing(true);
            const res = await onFetchExamDetails(examId);

            if (res) {
                await handleListTableChange();
            }
            setProcessing(false);
        };

        fetch();

        return () => {
            onClearExamApplications();
            onClearExamDetails();
        };
    }, [examId]);

    React.useEffect(() => {
        let timeout: NodeJS.Timeout | null = null;

        if (examDetails && examDetails.status === ExamStatus.Finishing) {
            if (prevStatus && prevStatus !== ExamStatus.Finishing) {
                window.location.reload();
            }

            timeout = setTimeout(async () => {
                await onFetchExamDetails(examId);
                await onFetchExamApplications({ examId, pageSize: 500 });
            }, 10 * 1000);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [examDetails]);

    React.useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (examDetails && examDetails.status === ExamStatus.IsPassing) {
            interval = setInterval(async () => {
                await onFetchExamApplications({ examId, pageSize: 500 });
            }, 20 * 1000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [examDetails]);

    React.useEffect(() => {
        if (examApplicationsList && examDetails && examDetails.status === ExamStatus.IsPassing && examDetails.type === ExamType.Online) {
            startReceiverQueue(examApplicationsList.items.map(a => a.applicantId));
        }
    }, [examDetails, examApplicationsList]);

    const handleSendMessage = useCallback(sendMessage.bind(null, (selectedApplicant ? selectedApplicant.applicantId : '')), [selectedApplicant]);

    const handleListTableChange = async (searchField: string | undefined = '', searchTerm: string | undefined = '') => {
        await onFetchExamApplications({
            examId,
            searchField,
            searchTerm,
            pageSize: 500,
            sortOrder: undefined,
            sortField: undefined,
        });
    };

    const isExamInitialized = () => {
        if (!examDetails) return false;

        switch (examDetails.status) {
            case ExamStatus.AwaitingExpertConfirmation:
            case ExamStatus.IsApplying:
            case ExamStatus.ApplyingFinished:
            case ExamStatus.Canceled:
            case ExamStatus.IsPassing:
                return true;
            default:
                return false;
        }
    };

    const handleAdmitToExam = React.useCallback(async () => {
        if (selectedApplicant) {
            await onAdmitToExam(selectedApplicant.examApplicationId);
            await onFetchExamApplications({ examId, pageSize: 500 });
        }
    }, [examId, selectedApplicant]);

    const handleFinishPracticeDefense = React.useCallback(async () => {
        if (selectedApplicant) {
            await finishPracticeDefense(selectedApplicant.examApplicationId);
            await onFetchExamApplications({ examId, pageSize: 500 });
        }
    }, [examId, selectedApplicant]);

    React.useEffect(() => {
        if (selectedApplicant) {
            const applicant = examApplicationsList && examApplicationsList.items.find(el => el.examApplicationId === selectedApplicant.examApplicationId || el.applicantId === selectedApplicant.applicantId);

            if (applicant) {
                setSelectedApplicant(applicant);
            }
        }
    }, [examApplicationsList]);


    const formattedExamApplicationsList = React.useMemo(() => {
        if (examApplicationsList && examDetails && examDetails.type === ExamType.Online) {
            // TODO (VP) вынести в редакс???
            return {
                ...examApplicationsList,
                items: examApplicationsList.items.map(a => (
                    {
                        ...a,
                        status: a.status === ExamApplicationStatus.Paid
                            ? (acceptedApplications.includes(a.applicantId) ? ExamApplicationStatus.WaitingForOnlineAccepting : ExamApplicationStatus.HaveNotOnlineAuth)
                            : a.status,
                    }
                )),
            };
        }

        return examApplicationsList || undefined;
    }, [examApplicationsList, examDetails, acceptedApplications]);

    const handleItemClick = React.useCallback(async (examApplicationId: string) => {
        const applicant = examApplicationsList && examApplicationsList.items.find(el => el.examApplicationId === examApplicationId || el.applicantId === examApplicationId);
        const formattedApplicant = formattedExamApplicationsList && applicant && formattedExamApplicationsList.items.find(el => el.applicantId === applicant.applicantId);
        const confirmExamStatus = Boolean(examDetails && examDetails.status !== ExamStatus.Finishing);
        const confirmApplicantStatus = Boolean(formattedApplicant && formattedApplicant.status !== ExamApplicationStatus.HaveNotOnlineAuth);

        if (applicant && confirmExamStatus && confirmApplicantStatus) {
            setSelectedApplicant(applicant);

            if (isOnlineModalStatus) {
                await onFetchExamApplicationDetails(applicant.examApplicationId);
                setOpenedOnlineModal(applicant.applicantId);
            } else {
                setShowApplicantModal(true);
            }
        }
    }, [examApplicationsList, formattedExamApplicationsList]);

    const onClickBack = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const from = urlParams.get('from');

        if (from) {
            onPush(`/${from}`);
        } else {
            onGoBack();
        }
    };

    const startExam = async () => {
        if (examApplicationsList) {
            const notAllApplicantsAdmitted = Boolean(examApplicationsList.items.find(applicant => applicant.status !== ExamApplicationStatus.AdmittedToExam));
            const res = await onFetchExamDetails(examId);

            if (res && res.status === ExamStatus.ApplyingFinished) {
                if (!notAllApplicantsAdmitted) {
                    setShowStartExamModal(true);
                } else {
                    setShowWarningModal(true);
                }
            }
        }
    };

    const onAccessStartExam = () => {
        onStartExam(examId);
    };

    const renderStartExamHeader = () => (examDetails ? (
        <>
            {examDetails.status === ExamStatus.ApplyingFinished ? (
                <>
                    <LoaderBtn loading={startExamProcessing} onClick={startExam} className={styles.start_exam_button}>
                        Начать экзамен
                    </LoaderBtn>
                    <WarningModal
                      show={showStartExamModal}
                      onAccess={onAccessStartExam}
                      onHide={() => setShowStartExamModal(false)}
                      title="Запуск экзамена"
                      accessBtnTittle="Да, начать экзамен сейчас"
                      bodyClassNames="pt-0"
                      closeBtnTittle="Отмена"
                    >
                        <span>
                            Перед началом экзамена убедитесь, что все соискатели находятся перед своими компьютерами
                            и не испытывают каких-либо технических затруднений.
                        </span>
                        <b className="d-block mt-4">Начать экзамен сейчас?</b>
                    </WarningModal>
                    <WarningModal
                      show={showWarningModal}
                      onAccess={onAccessStartExam}
                      onHide={() => setShowWarningModal(false)}
                      title="Не все соискатели допущены к экзамену"
                      accessBtnTittle="Да, начать экзамен сейчас"
                      bodyClassNames="pt-0"
                      closeBtnTittle="Отмена"
                    >
                        <span>
                            Для того чтобы соискатели могли начать сдачу экзамена,<br />
                            необходимо сначала проверить их документы и допустить к экзамену.<br />
                            Вы также сможете допускать опоздавших соискателей уже после начала экзамена.
                        </span>
                        <b className="d-block mt-4">Вы уверены, что хотите начать экзамен без допуска всех соискателей?</b>
                    </WarningModal>
                </>
            ) : null}

            {examDetails.status === ExamStatus.IsPassing && !(examDetails.practiceDefense && examDetails.type === ExamType.Online) && examDetails.actualStartTime ? (
                <div className={styles.detailsTimeLeft}>
                    <span className="pr-2">Осталось</span>
                    <DetailsTimeLeft
                      actualStartTime={examDetails.actualStartTime}
                      duration={examDetails.duration}
                      timeoutFunc={() => onFetchExamDetails(examId)}
                    />
                </div>
            ) : null}
        </>
    ) : null);


    const onCloseApplicantModal = React.useCallback(() => {
        if (selectedApplicant && examDetails && examDetails.type === ExamType.Online && examDetails.status !== ExamStatus.IsPassing) {
            stopReceiver(selectedApplicant.applicantId);
        }

        setSelectedApplicant(null);

        if (isOnlineModalStatus) {
            clearMessages();
            setOpenedOnlineModal(null);
        } else {
            setShowApplicantModal(false);
        }
    }, [selectedApplicant, examDetails]);

    const title = examDetails ? examDetails.qualificationTitle : '';

    const chairman = (examDetails && auth && examDetails.experts.some(c => c.expertId === auth.profile.id && c.chairman)) || false;

    return (
        <Card
          title={title}
          onClickBack={onClickBack}
          apiError={apiError}
          headerChild={renderStartExamHeader()}
          show404Error={!examDetails}
          showLoader={processing}
          smallTitle
          withBackButton
          withoutPadding
          withoutTitleMargin
        >
            {examDetails ? (
                <ExamDetailsTabs
                  appType={AppType.Expert}
                  examDetails={examDetails}
                  examApplicationsList={formattedExamApplicationsList}
                  applicantsTabProps={{
                        handleApplicantsListChange: handleListTableChange,
                        handleApplicantClick: handleItemClick,
                    }}
                  protocolModalProps={{
                        authorizedExpert: auth && auth.profile,
                        smsCodeStatus,
                        onSendNokPasswordAgain,
                        onSendAkPasswordAgain,
                        onSendSovietPasswordAgain,
                        onSubmitNokSignCode,
                        onSubmitAkSignCode,
                        onSubmitSovietSignCode,
                        sendCodeProcessing,
                        apiError,
                    }}
                  webRTC={webRTC}
                  unread={unread}
                  switchVideoType={switchVideoType}
                  onPreviewClick={handleItemClick}
                />
            ) : null}

            {selectedApplicant && examDetails && isOnlineModalStatus ? (
                <OnlineModal
                  examStatus={examDetails.status}
                  examApplicationStatus={selectedApplicant.status}
                  examApplicationDetails={examApplicationDetails}
                  practiceDefense={examDetails.practiceDefense}
                  examSessionReadyStatus={selectedApplicant.examSessionReadyStatus}
                  expertId={auth ? auth.profile.id : undefined}
                  chairman={chairman}
                  show={!!openedOnlineModal}
                  onClose={onCloseApplicantModal}
                  title={selectedApplicant.applicantFullName}
                  messages={messages}
                  onGetMessages={getMessages}
                  onSendMessage={handleSendMessage}
                  webRTC={webRTC[selectedApplicant.applicantId]}
                  onStart={startReceiver}
                  onSwitchType={switchVideoType}
                  applicantId={selectedApplicant.applicantId}
                  examApplicationId={selectedApplicant.examApplicationId}
                  onAdmitToExam={handleAdmitToExam}
                  onFinishPracticeDefense={handleFinishPracticeDefense}
                  onReadMessages={onMarkAsRead}
                />
            ) : selectedApplicant ? (
                <WarningModal
                  show={showApplicantModal}
                  onHide={onCloseApplicantModal}
                  title={`${selectedApplicant.applicantFullName}`}
                  dialogClassName={styles.applicantModal}
                  footerClassNames="p-0"
                  bodyClassNames="pb-0"
                  withoutTitleMargin={!(isExamInitialized() || selectedApplicant.status === ExamApplicationStatus.Absent)}
                  withoutCancelButton
                >
                    {isExamInitialized() || selectedApplicant.status === ExamApplicationStatus.Absent ? (
                        <ExamApplicationDetailsContainer
                          applicantId={selectedApplicant.examApplicationId}
                          onCloseClick={onCloseApplicantModal}
                          withoutCard
                        />
                    ) : (
                            <ApplicationExamDetailsContainer
                              examApplicationId={selectedApplicant.examApplicationId}
                              onCloseClick={onCloseApplicantModal}
                            />
                        )}
                </WarningModal>
            ) : null}
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    examDetails: state.exams.examDetails,
    smsCodeStatus: state.exams.smsCodeStatus,
    examApplicationsList: state.examApplications.examApplicationsList,
    auth: state.auth.auth,
    apiError: state.errors.error,
    messages: state.online.messages,
    startExamProcessing: Boolean(state.loading.effects.exams.startExam),
    sendCodeProcessing: Boolean(state.loading.effects.exams.submitNokSignCode
        || state.loading.effects.exams.submitAkSignCode
        || state.loading.effects.exams.submitSovietSignCode),
    webRTC: state.online.webRTC,
    acceptedApplications: state.online.acceptedApplications,
    unread: state.online.unread,
    openedOnlineModal: state.online.openedOnlineModal,
    examApplicationDetails: state.examApplications.examApplicationDetails,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onGoBack: () => dispatch(goBack()),
    onPush: (key: string) => dispatch(push(key)),
    sendMessage: (applicantId: string, message: string) => dispatch.online.sendMessageAsync({ applicantId, message }),
    clearMessages: () => dispatch.online.setMessages([]),
    startReceiver: (presenterId: string) => dispatch.online.handlePeerConnectionConnect({ presenterId }),
    switchVideoType: (videoType: VideoType, presenterId: string) => dispatch.online.switchVideoType({ videoType, presenterId }),
    onAdmitToExam: dispatch.examApplications.admitToExam,
    finishPracticeDefense: dispatch.examApplications.finishPracticeDefense,
    onMarkAsRead: dispatch.online.markAsRead,
    onClearExamApplications: dispatch.examApplications.clearExamApplications,
    onFetchExamApplications: dispatch.examApplications.fetchExamApplications,
    onClearExamDetails: dispatch.exams.clearExamDetails,
    onFetchExamDetails: dispatch.exams.fetchExamDetails,
    onStartExam: dispatch.exams.startExam,
    onSendAkPasswordAgain: dispatch.exams.sendAkPasswordAgain,
    onSendNokPasswordAgain: dispatch.exams.sendNokPasswordAgain,
    onSendSovietPasswordAgain: dispatch.exams.sendSovietPasswordAgain,
    onSubmitNokSignCode: dispatch.exams.submitNokSignCode,
    onSubmitAkSignCode: dispatch.exams.submitAkSignCode,
    onSubmitSovietSignCode: dispatch.exams.submitSovietSignCode,
    getMessages: dispatch.online.getMessagesAsync,
    setErrorHandler: dispatch.errors.setErrorHandler,
    startReceiverQueue: dispatch.online.startConnectionForApplicantIds,
    stopReceiver: dispatch.online.stopReceiver,
    stopAll: dispatch.online.stopAll,
    setOpenedOnlineModal: dispatch.online.setOpenedOnlineModal,
    onFetchExamApplicationDetails: dispatch.examApplications.fetchExamApplicationDetails,
});

export default connect(mapState, mapDispatch)(ExamDetailsContainer);

interface ExamsDetailsOwnProps {
    examId: string;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>

type Props = connectedProps & ExamsDetailsOwnProps;
